import { useParams, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  usePlannedWorkout,
  usePlannedWorkouts,
  useProfile,
  useRacePlan,
  useWorkouts,
} from '../hooks/useProfile';
import {
  dayjs,
  formatOptionalMiles,
  hasDisplayValue,
  parsePlannedDate,
} from '../lib/utils';
import {
  Link2,
  Unlink,
  Check,
  Calendar,
  PencilLine,
  Trash2,
  UploadCloud,
  Copy,
  Share2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Modal from '../components/Modals/Modal';
import PlannedWorkoutPlan from '../components/PlannedWorkoutPlan';
import { usePublishWorkoutToGarmin } from '../hooks/mutations/usePublishWorkoutToGarmin';
import { useDeletePlannedWorkout } from '../hooks/mutations/useDeletePlannedWorkout';
import { useGarminOAuth } from '../hooks/useGarminOAuth';
import {
  useLinkPlannedWorkout,
  useUnlinkPlannedWorkout,
} from '../hooks/mutations/usePlannedWorkoutLink';
import WorkoutActionBar from '../components/WorkoutActionBar';
import { useSharePlannedWorkout } from '../hooks/mutations/useSharePlannedWorkout';

const splitMetricValue = (value: unknown, fallbackUnit: string) => {
  if (!hasDisplayValue(value))
    return { value: '—', unit: fallbackUnit.toUpperCase() };

  const display = String(value).trim();
  const match = display.match(/^([\d:.]+)\s*(.*)$/);

  if (!match) return { value: display, unit: fallbackUnit };

  return {
    value: match[1],
    unit: (match[2] || fallbackUnit).toUpperCase(),
  };
};

const durationMetricValue = (value: unknown) => {
  if (!hasDisplayValue(value)) return '—';

  const match = String(value).match(/\d+(?:[.:]\d+)*/);
  return match?.[0] || '—';
};

const PlannedWorkoutDetail = () => {
  const { id } = useParams({ from: '/plan/workout/$id' });
  const navigate = useNavigate();
  const { data: racePlan, isLoading: planLoading } = useRacePlan();
  const { data: plannedWorkouts, isLoading: plannedLoading } =
    usePlannedWorkouts({});
  const { data: directWorkout, isLoading: directLoading } =
    usePlannedWorkout(id);
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { data: workoutData, isLoading: workoutsLoading } = useWorkouts();
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  const [showUnlinkConfirmation, setShowUnlinkConfirmation] = useState(false);
  const [showGarminConfirmation, setShowGarminConfirmation] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const sharePlannedWorkout = useSharePlannedWorkout();
  const publishWorkoutToGarmin = usePublishWorkoutToGarmin();
  const { connect: connectGarmin, isStarting: isStartingGarmin } =
    useGarminOAuth();
  const deletePlannedWorkout = useDeletePlannedWorkout({
    onSuccess: () => navigate({ to: '/plan' }),
  });
  const linkPlannedWorkout = useLinkPlannedWorkout({
    onSuccess: () => setShowLinkPicker(false),
  });
  const unlinkPlannedWorkout = useUnlinkPlannedWorkout();

  const allPlannedWorkouts =
    racePlan?.training_plan?.flatMap((w: any) => w.workouts) || [];
  const workout =
    allPlannedWorkouts.find((w: any) => w.id === parseInt(id)) ||
    plannedWorkouts?.find((w: any) => w.id === parseInt(id)) ||
    directWorkout;

  const completedWorkouts = workoutData?.workouts || [];
  const linkedWorkout = workout?.activity_id
    ? completedWorkouts.find((w: any) => w.id === workout.activity_id)
    : null;
  const hasGarminConnection =
    profile?.user?.connections?.some(
      (connection: any) => connection.connectionType === 'garmin',
    ) || false;

  const handleLink = (completedWorkoutId: number) => {
    linkPlannedWorkout.mutate({ workout, completedWorkoutId });
  };

  const handleUnlink = () => {
    unlinkPlannedWorkout.mutate(
      { workout },
      { onSuccess: () => setShowUnlinkConfirmation(false) },
    );
  };

  const handleDelete = () => {
    if (!confirm('Delete this planned workout? This cannot be undone.')) return;
    deletePlannedWorkout.mutate({ workoutId: workout.id });
  };

  const handleGarminConfirmation = () => {
    if (hasGarminConnection) {
      publishWorkoutToGarmin.mutate(
        { workoutId: workout.id },
        { onSuccess: () => setShowGarminConfirmation(false) },
      );
      return;
    }

    void connectGarmin(`/plan/workout/${workout.id}`);
  };

  const handleShare = async () => {
    try {
      const result = await sharePlannedWorkout.mutateAsync({
        workoutId: workout.id,
      });
      const prefersNativeShare = window.matchMedia(
        '(hover: none) and (pointer: coarse)',
      ).matches;
      if (prefersNativeShare && navigator.share) {
        await navigator.share({
          title: workout.title || workout.type,
          text: 'Save this planned workout on RaceTrainer',
          url: result.url,
        });
        return;
      }
      await navigator.clipboard.writeText(result.url);
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 2500);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      window.dispatchEvent(new CustomEvent('showToast', { detail: {
        message: error instanceof Error ? error.message : 'Unable to share workout',
        type: 'error',
      } }));
    }
  };

  if (
    planLoading ||
    workoutsLoading ||
    profileLoading ||
    plannedLoading ||
    directLoading
  ) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!workout) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <div className="text-gray-500">Workout not found</div>
        <Button variant="outline" onClick={() => navigate({ to: '/plan' })}>
          Back to Plan
        </Button>
      </div>
    );
  }

  const workoutDate = parsePlannedDate(workout.planned_date);
  const workoutTitle = [workout.type, workout.day ? `Day ${workout.day}` : null]
    .filter((part) => hasDisplayValue(part) && String(part).trim() !== '0')
    .join(' - ');
  const isRestDay = String(workout.type || '')
    .toLowerCase()
    .includes('rest');
  const isStrengthWorkout = String(workout.type || '')
    .toLowerCase()
    .includes('strength');
  const distanceMetric = splitMetricValue(workout.distance, 'mi');
  const duration = durationMetricValue(workout.total_time);

  const nearbyCompleted = completedWorkouts
    .filter((w: any) => {
      if (linkedWorkout?.id === w.id) return false;
      const wDate = dayjs(w.completedAt);
      return Math.abs(wDate.diff(workoutDate, 'days')) <= 3;
    })
    .sort((a: any, b: any) => {
      const aDistance = Math.abs(dayjs(a.completedAt).diff(workoutDate, 'day'));
      const bDistance = Math.abs(dayjs(b.completedAt).diff(workoutDate, 'day'));
      return aDistance - bDistance;
    });

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-1 pb-6 md:px-0">
      <WorkoutActionBar
        backLabel="Back to plan"
        onBack={() => navigate({ to: '/plan' })}
        actions={[
          {
            label: shareCopied
              ? 'Link copied'
              : sharePlannedWorkout.isPending
                ? 'Creating share link'
                : 'Share workout',
            icon: shareCopied ? (
              <Check className="h-5 w-5" />
            ) : (
              <Share2 className="h-5 w-5" />
            ),
            onClick: handleShare,
            disabled: sharePlannedWorkout.isPending,
          },
          {
            label: 'Duplicate workout',
            icon: <Copy className="h-5 w-5" />,
            onClick: () =>
              navigate({
                to: '/plan/workout/$id/duplicate',
                params: { id: String(workout.id) },
              }),
          },
          ...(!isRestDay
            ? [
                workout.activity_id
                  ? {
                      label: 'Unlink workout',
                      icon: <Unlink className="h-5 w-5" />,
                      onClick: () => {
                        unlinkPlannedWorkout.reset();
                        setShowUnlinkConfirmation(true);
                      },
                      destructive: true,
                    }
                  : {
                      label: 'Link workout',
                      icon: <Link2 className="h-5 w-5" />,
                      onClick: () => {
                        linkPlannedWorkout.reset();
                        setShowLinkPicker(true);
                      },
                    },
              ]
            : []),
          ...(!workout.completed && !isRestDay
            ? [
                {
                  label: 'Edit workout',
                  icon: <PencilLine className="h-5 w-5" />,
                  onClick: () =>
                    navigate({
                      to: '/plan/workout/$id/edit',
                      params: { id: String(workout.id) },
                    }),
                },
                {
                  label: publishWorkoutToGarmin.isPending
                    ? 'Sending workout to Garmin'
                    : 'Send workout to Garmin',
                  icon: <UploadCloud className="h-5 w-5" />,
                  onClick: () => setShowGarminConfirmation(true),
                  disabled: publishWorkoutToGarmin.isPending,
                },
                {
                  label: deletePlannedWorkout.isPending
                    ? 'Deleting workout'
                    : 'Delete workout',
                  icon: <Trash2 className="h-5 w-5" />,
                  onClick: handleDelete,
                  disabled: deletePlannedWorkout.isPending,
                  destructive: true,
                },
              ]
            : []),
        ]}
      />

      {/* Title and date */}
      <div className="detail-heading clear-both">
        <p className="eyebrow">Planned session</p>
        <div className="flex items-center gap-2 mb-2">
          <h1 className="text-3xl font-bold tracking-tight text-brand-midnight capitalize">
            {workoutTitle || 'Workout'}
          </h1>
          {workout.completed ? (
            <span className="px-2 py-0.5 bg-brand-greenSoft text-brand-midnight text-xs font-medium rounded-full flex items-center gap-1">
              <Check className="h-3 w-3" />
              Completed
            </span>
          ) : null}
          {workout.activity_id ? (
            <span className="flex items-center gap-1 rounded-full bg-brand-blueSoft px-2 py-0.5 text-xs font-medium text-brand-midnight">
              <Link2 className="h-3 w-3" />
              Linked
            </span>
          ) : null}
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-gray-500 flex items-center gap-1.5">
            <Calendar className="h-4 w-4" />
            {workoutDate.format('dddd, MMMM D, YYYY')}
          </p>
        </div>
      </div>

      {isRestDay ? (
        <div className="rounded-2xl border border-brand-blueSoft bg-brand-blueSoft/60 p-6 shadow-sm">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-brand-cobalt">
            Recovery Day
          </p>
          <h2 className="mt-2 text-2xl font-black tracking-[-0.03em] text-brand-midnight">
            Absorb the work
          </h2>
          <p className="mt-3 text-sm leading-6 text-brand-slate">
            Keep movement optional and easy today. Prioritize sleep, fueling,
            mobility, and hydration so the next quality session feels sharp.
          </p>
        </div>
      ) : (
        <div
          className={`${isStrengthWorkout ? 'grid-cols-1' : 'grid-cols-2'} workout-stat-grid surface-card grid gap-4 p-6`}
        >
          {isStrengthWorkout ? null : (
            <div className="text-center">
              <div className="mb-1 text-xs text-gray-500">Distance</div>
              <div className="text-2xl font-black text-gray-900">
                {distanceMetric.value}
              </div>
              <div className="mt-0.5 text-[11px] font-black uppercase tracking-wide text-gray-500">
                {distanceMetric.unit}
              </div>
            </div>
          )}
          <div className="text-center">
            <div className="mb-1 text-xs text-gray-500">Duration</div>
            <div className="text-2xl font-black text-gray-900">{duration}</div>
            <div className="mt-0.5 text-[11px] font-black uppercase tracking-wide text-gray-500">
              MINS
            </div>
          </div>
        </div>
      )}

      {publishWorkoutToGarmin.isError ? (
        <div className="rounded-xl border border-brand-blueSoft bg-brand-blueSoft/50 p-4 text-sm font-medium text-brand-midnight">
          {publishWorkoutToGarmin.error.message}
        </div>
      ) : null}

      {deletePlannedWorkout.isError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">
          {deletePlannedWorkout.error.message}
        </div>
      ) : null}

      <PlannedWorkoutPlan
        details={workout.details}
        intervals={workout.intervals}
      />

      {/* Linked workout */}
      {workout.activity_id ? (
        <div className="bg-brand-greenSoft rounded-xl p-6">
          <h3 className="mb-3 text-sm font-medium text-brand-midnight">
            Linked Workout
          </h3>
          {linkedWorkout ? (
            <button
              onClick={() =>
                navigate({
                  to: '/workouts/$id',
                  params: { id: linkedWorkout.id },
                })
              }
              className="block w-full text-left"
            >
              <div className="flex items-center justify-between bg-white rounded-lg p-4 hover:shadow transition-shadow">
                <div>
                  <p className="font-medium text-gray-900">
                    {linkedWorkout.title || 'Untitled'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {dayjs(linkedWorkout.completedAt).format('MMM D, YYYY')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">
                    {formatOptionalMiles(linkedWorkout.distance)}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">
                    {linkedWorkout.type}
                  </p>
                </div>
              </div>
            </button>
          ) : (
            <p className="rounded-lg bg-white p-4 text-sm text-gray-600">
              This planned workout is linked to completed workout #
              {workout.activity_id}.
            </p>
          )}
        </div>
      ) : null}

      {/* Link picker */}
      <Modal
        show={showLinkPicker}
        onClose={() => setShowLinkPicker(false)}
        title="Link a completed workout"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Choose a workout completed within 3 days of the planned date.
          </p>
          {nearbyCompleted.length === 0 ? (
            <p className="text-sm text-gray-500">
              No nearby workouts found. Complete a workout within 3 days of this
              planned date to link it.
            </p>
          ) : (
            <div className="space-y-2">
              {nearbyCompleted.map((w: any) => (
                <button
                  key={w.id}
                  onClick={() => handleLink(w.id)}
                  disabled={linkPlannedWorkout.isPending}
                  className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-brand-offWhite transition-colors"
                >
                  <div className="text-left">
                    <p className="font-medium text-gray-900 text-sm">
                      {w.title || 'Untitled'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {dayjs(w.completedAt).format('MMM D, YYYY')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">
                      {formatOptionalMiles(w.distance)}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">{w.type}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
          {linkPlannedWorkout.isError ? (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
            >
              {linkPlannedWorkout.error.message}
            </div>
          ) : null}
        </div>
      </Modal>

      <Modal
        show={showGarminConfirmation}
        onClose={() => setShowGarminConfirmation(false)}
        title={
          hasGarminConnection ? 'Send workout to Garmin?' : 'Connect Garmin?'
        }
      >
        <div className="space-y-5">
          <p className="text-sm text-gray-600">
            {hasGarminConnection
              ? 'This workout will be published to your connected Garmin account.'
              : 'Connect your Garmin device to publish this workout. After connecting, you will return to this planned workout and can send it to Garmin.'}
          </p>
          {publishWorkoutToGarmin.isError ? (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
            >
              {publishWorkoutToGarmin.error.message}
            </div>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowGarminConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGarminConfirmation}
              disabled={publishWorkoutToGarmin.isPending || isStartingGarmin}
            >
              {hasGarminConnection
                ? publishWorkoutToGarmin.isPending
                  ? 'Publishing...'
                  : 'Publish workout'
                : isStartingGarmin
                  ? 'Connecting...'
                  : 'Connect Garmin'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        show={showUnlinkConfirmation}
        onClose={() => setShowUnlinkConfirmation(false)}
        title="Unlink completed workout?"
      >
        <div className="space-y-5">
          <p className="text-sm text-gray-600">
            This removes the completed workout from this planned workout and
            marks the planned workout as incomplete.
          </p>
          {unlinkPlannedWorkout.isError ? (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
            >
              {unlinkPlannedWorkout.error.message}
            </div>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowUnlinkConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleUnlink}
              disabled={unlinkPlannedWorkout.isPending}
            >
              {unlinkPlannedWorkout.isPending
                ? 'Unlinking...'
                : 'Unlink workout'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PlannedWorkoutDetail;
